// MACHINERY V2 (Stroje) — fleet derivation helpers (M2.2).
//
// Pure functions only: no React, no dispatch, no fetching. Extracted from
// FleetV1Page so the page stays a composition and each rule here is testable in
// isolation (and so both files stay under the 300-line cap).
//
// PARITY NOTE: the grouping + client-side decommissioned filtering below are
// TRANSCRIBED from the live grid (components/Machinery.tsx) so v2 groups and
// searches identically — same dimensions, same "Ungrouped"/"All Machines"
// buckets, same 4-field decommissioned search (model / manufacturer.name /
// serialNumber / customBrandName). Rearranged, never re-decided.
//
// STATUS COUNTS COME FROM THE CONTRACT, NEVER FROM RAW FIELDS: countStates()
// takes states already resolved by deriveCardPresentation(). Nothing in this
// file tests `alarms.length` or `status === "alarm"`.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { Machine } from "../../models/models";
import {
  MachineryV2CardState,
  MachineryV2Telemetry,
  deriveCardPresentation,
  unconnectedTelemetry,
} from "./cardStates";

export type FleetGroups = Record<string, Machine[]>;

/**
 * Flatten a group-name -> machines map into one list of DISTINCT machines.
 *
 * DE-DUPLICATION IS LOAD-BEARING (found while building M2.3). A machine can
 * legitimately belong to SEVERAL groups: for `line` / `department`,
 * groupMachinesClientSide keys on `groupNames`, which is an array, and pushes
 * the machine under EVERY name — the server's `groupBy` response does the same.
 * A naive `Object.values().flat()` therefore returned that machine twice, and
 * because this list is the base for `stateCounts`, `typeCounts`,
 * `buildLocationTree`, `derivedManufacturers`, the "All" rail total, the
 * LiveFleetStrip total and `visibleCount`, EVERY one of those inflated as soon
 * as the grouping was line/department. M2.3's per-group health counts would
 * have inherited it.
 *
 * The grouped MAP keeps its duplicates on purpose — a machine in two lines
 * really does belong in both sections. Only the flat "how many machines are
 * there" view collapses them.
 */
export const flattenGroups = (groups?: FleetGroups | null): Machine[] => {
  if (!groups) return [];
  const seen = new Set<string>();
  const out: Machine[] = [];
  for (const list of Object.values(groups)) {
    for (const m of list) {
      if (m && !seen.has(m.id)) {
        seen.add(m.id);
        out.push(m);
      }
    }
  }
  return out;
};

// ---------------------------------------------------------------------
//  Card state per machine — ONE derivation, reused by counts + filters
// ---------------------------------------------------------------------

/**
 * Resolve every on-page machine's canonical card state THROUGH the contract.
 * The fleet card re-derives its own full presentation from the same telemetry;
 * both calls go through deriveCardPresentation, so they cannot disagree.
 */
export const deriveFleetStates = (
  machines: Machine[],
  telemetry: Map<string, MachineryV2Telemetry>
): Map<string, MachineryV2CardState> => {
  const out = new Map<string, MachineryV2CardState>();
  for (const m of machines) {
    const t = telemetry.get(m.id) || unconnectedTelemetry();
    out.set(m.id, deriveCardPresentation(t).state);
  }
  return out;
};

export const countStates = (
  machines: Machine[],
  states: Map<string, MachineryV2CardState>
): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (const m of machines) {
    const s = states.get(m.id);
    if (!s) continue;
    counts[s] = (counts[s] || 0) + 1;
  }
  return counts;
};

// ---------------------------------------------------------------------
//  Location hierarchy (LOC-4) — transcribed from Machinery.tsx
// ---------------------------------------------------------------------

export interface FleetLocationNode {
  /** Parent nodes key on the location NAME; children on "parent>child". */
  id: string;
  name: string;
  count: number;
  children: FleetLocationNode[];
}

export const buildLocationTree = (machines: Machine[]): FleetLocationNode[] => {
  const parents = new Map<string, { count: number; children: Map<string, number> }>();

  for (const m of machines) {
    if (!m.locationName) continue;
    const key = m.parentLocationName || m.locationName;
    if (!parents.has(key)) parents.set(key, { count: 0, children: new Map() });
    const node = parents.get(key)!;
    node.count += 1;
    if (m.parentLocationName) {
      node.children.set(m.locationName, (node.children.get(m.locationName) || 0) + 1);
    }
  }

  return Array.from(parents.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([name, data]) => {
      const children = Array.from(data.children.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([childName, count]) => ({
          id: name + ">" + childName,
          name: childName,
          count,
          children: [],
        }));
      return { id: name, name, count: data.count, children };
    });
};

/**
 * Does a machine sit under a location-tree node id?
 *
 * §9 D4 CARRIES the location filter, and this is the point at which it stops
 * being the live grid's "VISUAL SCAFFOLD — does not yet filter" (data package
 * §9) and actually filters. Same tree, same ids, same counts — the rail's
 * checkbox now does what it always looked like it did.
 */
export const machineInLocation = (m: Machine, nodeId: string): boolean => {
  if (!m.locationName) return false;
  const sep = nodeId.indexOf(">");
  if (sep >= 0) {
    return (m.parentLocationName || "") + ">" + m.locationName === nodeId;
  }
  return m.parentLocationName ? m.parentLocationName === nodeId : m.locationName === nodeId;
};

// ---------------------------------------------------------------------
//  Grouping + client-side filtering (decommissioned scope)
// ---------------------------------------------------------------------

/**
 * Group a flat list by the current dimension. Verbatim rules from the live
 * grid's groupMachinesClientSide (type / location / line / department / all).
 *
 * MULTI-GROUP MEMBERSHIP IS INTENTIONAL. `line` / `department` key on the
 * `groupNames` ARRAY, so the loop below pushes one machine into EVERY section
 * it belongs to — a machine on two lines really does belong in both, and the
 * V2 group health headers count it in both, correctly, because each header
 * counts its own section.
 *
 * It is still ONE machine in every FLEET-WIDE count: `flattenGroups` de-dupes
 * by id, and the facet counts, the rail totals, the LiveFleetStrip and
 * `visibleCount` all derive from that flat set. Duplicates in the MAP, distinct
 * machines in the COUNTS — do not "fix" either half to match the other.
 */
export const groupMachinesClientSide = (
  flat: Machine[],
  dimension: string
): FleetGroups => {
  const result: FleetGroups = {};
  for (const m of flat) {
    let keys: string[];
    switch (dimension) {
      case "type":
        keys = [m.type?.name || "Unknown"];
        break;
      case "location":
        keys = m.locationName
          ? [m.parentLocationName ? m.parentLocationName + " > " + m.locationName : m.locationName]
          : ["Ungrouped"];
        break;
      case "line":
      case "department":
        keys = m.groupNames && m.groupNames.length > 0 ? m.groupNames : ["Ungrouped"];
        break;
      default:
        keys = ["All Machines"];
        break;
    }
    for (const key of keys) {
      if (!result[key]) result[key] = [];
      result[key].push(m);
    }
  }
  return result;
};

export interface DecommissionedFilter {
  search?: string;
  typeId?: string;
  manufacturerId?: string;
}

/** Client-side filter for the decommissioned scope — the live grid's exact
 *  4-field search (model / manufacturer.name / serialNumber / customBrandName).
 *  Called with SEARCH ONLY now: type/manufacturer are facets and are applied by
 *  applyClientFilters, so the decommissioned rail counts the same base set the
 *  active scope does. */
export const filterDecommissioned = (
  machines: Machine[],
  q: DecommissionedFilter
): Machine[] => {
  let out = machines;
  if (q.search) {
    const needle = q.search.toLowerCase();
    out = out.filter(
      (m) =>
        (m.model && m.model.toLowerCase().includes(needle)) ||
        (m.manufacturer?.name && m.manufacturer.name.toLowerCase().includes(needle)) ||
        (m.serialNumber && m.serialNumber.toLowerCase().includes(needle)) ||
        (m.customBrandName && m.customBrandName.toLowerCase().includes(needle))
    );
  }
  if (q.typeId) out = out.filter((m) => m.type?.id === q.typeId);
  if (q.manufacturerId) out = out.filter((m) => m.manufacturer?.id === q.manufacturerId);
  return out;
};

/** Manufacturer sub-filter options, derived from the user's OWN machines of the
 *  selected type (never from the catalog API) — the live grid's rule. */
export const derivedManufacturers = (
  machines: Machine[],
  typeId?: string
): { id: string; name: string }[] => {
  if (!typeId) return [];
  const seen = new Map<string, { id: string; name: string }>();
  for (const m of machines) {
    if (m.type?.id === typeId && m.manufacturer?.id && !seen.has(m.manufacturer.id)) {
      seen.set(m.manufacturer.id, { id: m.manufacturer.id, name: m.manufacturer.name });
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export interface FleetClientFilters {
  statusFilters: MachineryV2CardState[];
  locationFilter: string | null;
  typeId?: string;
  manufacturerId?: string;
}

/** Apply EVERY facet client-side (status, location, type, manufacturer) over
 *  the search-filtered base set. See the faceting note in the file header for
 *  why type/manufacturer moved off the query string. */
export const applyClientFilters = (
  groups: FleetGroups,
  states: Map<string, MachineryV2CardState>,
  f: FleetClientFilters
): FleetGroups => {
  const active =
    f.statusFilters.length > 0 || !!f.locationFilter || !!f.typeId || !!f.manufacturerId;
  if (!active) return groups;
  const out: FleetGroups = {};
  for (const [name, list] of Object.entries(groups)) {
    const kept = list.filter((m) => {
      if (f.typeId && m.type?.id !== f.typeId) return false;
      if (f.manufacturerId && m.manufacturer?.id !== f.manufacturerId) return false;
      if (f.locationFilter && !machineInLocation(m, f.locationFilter)) return false;
      if (f.statusFilters.length === 0) return true;
      const s = states.get(m.id);
      return !!s && f.statusFilters.indexOf(s) >= 0;
    });
    if (kept.length > 0) out[name] = kept;
  }
  return out;
};
